import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './StaticPage.css';

function CareersPage() {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="static-page">
      <nav className={`nav ${scrolled ? 'nav-scrolled' : ''}`}>
        <div className="nav-container">
          <a href="/" className="nav-logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
          <div className="nav-links">
            <a href="/">Home</a>
            <a href="/platform">Platform</a>
            <a href="/how-it-works">How it Works</a>
            <a href="/use-cases">Use Cases</a>
            <a href="/consulting">Consulting</a>
          </div>
          <div className="nav-actions">
            <button className="btn-ghost" onClick={() => navigate('/login')}>Sign In</button>
            <button className="btn-primary" onClick={() => navigate('/request-demo')}>Request a Demo</button>
          </div>
        </div>
      </nav>

      <section className="sp-hero">
        <div className="sp-container">
          <h1 className="sp-hero-title">Careers</h1>
          <p className="sp-hero-text">
            Help us build the cognitive layer that makes enterprise AI actually work.
          </p>
        </div>
      </section>

      <section className="sp-section">
        <div className="sp-container">
          <h2 className="sp-section-title">Why CerebralMachines?</h2>
          <p className="sp-text">
            We are solving one of the hardest and most impactful problems in enterprise AI:
            giving organizations the ability to encode, control, and govern intelligence at scale.
            If you want to work on problems that matter — with technology that ships to production
            and transforms how the world's largest enterprises operate — this is the place.
          </p>
          <div className="sp-cards">
            <div className="sp-card">
              <h3>Hard Problems, Real Impact</h3>
              <p>
                We work on semantic computing, cognitive reasoning, and deterministic AI
                at enterprise scale. Every line of code you write has the potential to power
                decisions in healthcare, finance, and government.
              </p>
            </div>
            <div className="sp-card">
              <h3>Small Team, Big Leverage</h3>
              <p>
                We are a lean, high-caliber team where every person has outsized impact.
                You will not be a cog in a machine — you will shape the product and the company.
              </p>
            </div>
            <div className="sp-card">
              <h3>Technical Excellence</h3>
              <p>
                We care deeply about engineering quality, system design, and shipping
                production-grade software. We hire people who hold themselves to high standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="sp-section sp-section-alt">
        <div className="sp-container">
          <h2 className="sp-section-title">What We Look For</h2>
          <ul className="sp-list">
            <li>Exceptional problem-solving ability and intellectual curiosity</li>
            <li>Strong fundamentals in software engineering, systems design, or AI research</li>
            <li>A bias toward shipping — prototypes are great, but production is better</li>
            <li>Comfort with ambiguity and the pace of an early-stage company</li>
            <li>Passion for building technology that enterprises can trust</li>
          </ul>
        </div>
      </section>

      <section className="sp-section">
        <div className="sp-container">
          <h2 className="sp-section-title">Open Positions</h2>
          <p className="sp-text">
            We are actively hiring across engineering, research, and go-to-market. If you don't
            see a role that fits but believe you'd be a strong addition to the team, we still
            want to hear from you.
          </p>
          <div className="sp-cards">
            <div className="sp-card">
              <h3>Senior Backend Engineer</h3>
              <p>Build and scale the core cognitive reasoning engine. Python, distributed systems, graph databases.</p>
            </div>
            <div className="sp-card">
              <h3>Frontend Engineer</h3>
              <p>Build intuitive interfaces for complex enterprise AI workflows. React, TypeScript, data visualization.</p>
            </div>
            <div className="sp-card">
              <h3>AI/ML Research Engineer</h3>
              <p>Advance our semantic reasoning and probabilistic inference capabilities. NLP, knowledge graphs, probabilistic models.</p>
            </div>
            <div className="sp-card">
              <h3>Solutions Engineer</h3>
              <p>Partner with enterprise customers to deploy cognitive AI in mission-critical workflows.</p>
            </div>
          </div>
          <p className="sp-emphasis">
            To apply, send your resume and a brief note about what excites you to careers@cerebralmachines.ai
          </p>
        </div>
      </section>

      <section className="sp-cta">
        <div className="sp-container">
          <h2 className="sp-cta-title">Ready to Make an Impact?</h2>
          <p className="sp-cta-text">
            Reach out and tell us what you'd build.
          </p>
          <button className="btn-primary-large" onClick={() => navigate('/contact')}>Get in Touch</button>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-container">
          <div className="footer-main">
            <div className="footer-brand">
              <a href="/" className="footer-logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
              <p className="footer-tagline">Building the cognitive backbone for enterprise AI.</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Product</h4>
                <ul>
                  <li><a href="/platform">Platform</a></li>
                  <li><a href="/how-it-works">How it Works</a></li>
                  <li><a href="/use-cases">Use Cases</a></li>
                  <li><a href="/consulting">Consulting</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About</a></li>
                  <li><a href="/team">Team</a></li>
                  <li><a href="/careers">Careers</a></li>
                  <li><a href="/contact">Contact</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Legal</h4>
                <ul>
                  <li><a href="/privacy">Privacy Policy</a></li>
                  <li><a href="/terms">Terms of Service</a></li>
                  <li><a href="/security">Security</a></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 CerebralMachines. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default CareersPage;
